import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../dataManagement/uiDb/UiDb";
import { productGalleryItems } from "../../../../../../services/helpers/ProductImageHelper";
import { useGallery } from "../../hooks/useGallery";
import { useCompactHeader } from "../../hooks/useCompactHeader";
import MobileGallery from "./MobileGallery";
import DesktopGallery from "./DesktopGallery";
import GalleryViewer from "./GalleryViewer";
import CompactHeaderV2 from "../HeaderSection/CompactHeaderV2";
import ShareProfileModal from "../../../../../shared/ShareProfileModal/ShareProfileModal";
import ReportContentModal from "../../../../../shared/ReportContentModal/ReportContentModal";
import genericUtility from "../../../../../../services/utility/GenericUtility";
import { isIosWebView } from "../../../../../../services/helpers/platform";

const GallerySection: React.FC = () => {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 768);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const productId = useSelector(
    (state: RootState) => state.productDetails.activeProductId ?? undefined
  );
  const product = useSelector((state: RootState) =>
    productId ? state.productDetails.data[productId] : undefined
  );
  const isAuthenticated = useSelector(
    (state: RootState) => !!state.profile.profileData?.userId
  );

  // Gallery items (images + videos, payload order) from productMediaResources
  // — the only media source. undefined while the product is still loading —
  // useGallery holds its loading state. Empty is definitive: no visual media.
  const payloadItems = useMemo(
    () => (product ? productGalleryItems(product) : undefined),
    [product]
  );

  const {
    items,
    imageError,
    currentIndex,
    setCurrentIndex,
    carouselRef,
    activeImgRef,
    handleCarouselScroll,
    onCarouselWheel,
    goToImage,
    isViewerOpen,
    openViewer,
    closeViewer,
    viewerIndex,
  } = useGallery(productId, payloadItems);

  const { scrollProgress, headerOpacity, showProductInfo } = useCompactHeader();

  const handleShare = () => setIsShareOpen(true);
  const handleReport = () => setIsReportOpen(true);

  // Reporting is an iOS-only control (App Store UGC-reporting compliance) — it
  // must be reachable to any user, including logged-out reviewers, so it is NOT
  // gated on login. Still hidden on your own listing.
  const currentUserId = genericUtility.getCookie("userId");
  const isOwnListing = !!product?.sellerId && product.sellerId === currentUserId;
  const canReport = isIosWebView() && !isOwnListing;

  const shareUrl = `${window.location.origin}/product-details/${productId}`;

  // Image-only derivations for surfaces that can't render a video:
  // compact-header thumbnail and the share modal's preview list.
  const imageUrls = useMemo(
    () => items.filter((i) => i.kind === "image").map((i) => i.url),
    [items]
  );
  const currentItem = items[currentIndex];
  const headerThumb =
    currentItem?.kind === "image" ? currentItem.url : imageUrls[0];

  const overlayHeader = (
    <CompactHeaderV2
      productImage={headerThumb}
      productTitle={product?.title}
      productPrice={product?.sellingPrice}
      productId={productId}
      opacity={Math.max(0, 1 - scrollProgress * 2)}
      showProductInfo={false}
      overlayMode
      isAuthenticated={isAuthenticated}
      onShare={handleShare}
      onReport={canReport ? handleReport : undefined}
    />
  );

  const fixedHeader = (
    <CompactHeaderV2
      productImage={headerThumb}
      productTitle={product?.title}
      productPrice={product?.sellingPrice}
      productId={productId}
      opacity={headerOpacity}
      showProductInfo={showProductInfo}
      overlayMode={false}
      isAuthenticated={isAuthenticated}
      onShare={handleShare}
      onReport={canReport ? handleReport : undefined}
    />
  );

  return (
    <>
      <ShareProfileModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        profileUrl={shareUrl}
        title={product?.title || "Share Product"}
        subtitle={`Check out ${product?.title || "this item"} on NineFinds`}
        productImages={imageUrls}
      />

      <ReportContentModal
        isOpen={isReportOpen}
        contentType="listing"
        targetName={product?.title ?? "this listing"}
        onClose={() => setIsReportOpen(false)}
      />

      {isMobile ? (
        <MobileGallery
          items={items}
          currentIndex={currentIndex}
          onIndexChange={setCurrentIndex}
          onOpenViewer={openViewer}
          imageError={imageError}
          headerOverlay={overlayHeader}
          headerFixed={fixedHeader}
        />
      ) : (
        <>
          {/* Desktop has a permanent compact header at top */}
          <CompactHeaderV2
            productTitle={product?.title}
            productId={productId}
            opacity={1}
            showProductInfo={false}
            overlayMode={false}
            isAuthenticated={isAuthenticated}
            onShare={handleShare}
            onReport={canReport ? handleReport : undefined}
          />
          <DesktopGallery
            items={items}
            currentIndex={currentIndex}
            carouselRef={carouselRef}
            activeImgRef={activeImgRef}
            onScroll={handleCarouselScroll}
            onWheel={onCarouselWheel}
            onGoToImage={goToImage}
            onOpenViewer={openViewer}
            onShare={handleShare}
          />
        </>
      )}

      <GalleryViewer
        items={items}
        initialIndex={viewerIndex}
        isOpen={isViewerOpen}
        onClose={closeViewer}
        productTitle={product?.title}
      />
    </>
  );
};

export default GallerySection;
